import { useCallback, useMemo, useState } from "react";
import { Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import {
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
  EEscalationStatus,
  UserProfile,
} from "../../../../../setup/api/services/contract/models";
import { EContractActions } from "../../../../../setup/api/services/contractActions/enum";
import { doAction } from "../../../../../setup/api/services/contractActions";
import { IUser } from "../../../../../setup/api/services/auth/models";
import useLocalStorage from "../../../../../hooks/useLocalStorage";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import OutlinedButton from "../../../../../components/common/OutlinedButton";
import Spinner from "../../../../../components/common/Spinner";
import RejectSettlementModal from "../../EscalationSection/RejectSettlementModal";

import styles from "./styles.module.scss";

interface EscalationActionsProps {
  milestone: IContractDetailsResponseData;
}

type Resolution = "RESOLVED_CUSTOMER" | "RESOLVED_PROVIDER";

const EscalationActions = ({ milestone }: EscalationActionsProps) => {
  const { t } = useTranslation();
  const [userInfo] = useLocalStorage<IUser | null>("userInfo", null);

  const [proposalOpen, setProposalOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Proposal form state
  const [resolution, setResolution] = useState<Resolution>("RESOLVED_CUSTOMER");
  const [rejectionComment, setRejectionComment] = useState("");
  const [showBalanceError, setShowBalanceError] = useState(false);

  const escalation = milestone.escalations?.find((e) => !e.resolved);
  const allowResolveDispute = milestone.organization?.allowResolveDispute;

  const paidAmount = useMemo(() => {
    return (
      milestone.transactions
        ?.filter(
          (tx) =>
            tx.type === ETrasactionsType.CUSTOMER_PAY && tx.status === "SUCCESS"
        )
        .reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0
    );
  }, [milestone.transactions]);

  const initialRows = useMemo(() => {
    const rows: {
      externalId: string;
      name: string;
      phone: string;
      role: string;
      amount: number | undefined;
    }[] = [];
    const push = (
      role: string,
      profiles: Partial<UserProfile>[] | undefined
    ) => {
      profiles?.forEach((p) => {
        rows.push({
          externalId: String(p.userIdentification || p.id || ""),
          name: p.profile?.fullName || "",
          phone: p.phoneNumber || "",
          role,
          amount: undefined,
        });
      });
    };
    if (milestone.partiesProfiles) {
      push("CUSTOMER", milestone.partiesProfiles.CUSTOMER || []);
      push("PROVIDER", milestone.partiesProfiles.PROVIDER || []);
      push("BROKER", milestone.partiesProfiles.BROKER || []);
    }
    return rows;
  }, [milestone.partiesProfiles]);

  const [rows, setRows] = useState(initialRows);

  const currentTotal = useMemo(
    () => rows.reduce((s, r) => s + (r.amount || 0), 0),
    [rows]
  );
  const remaining = useMemo(
    () => Number((paidAmount - currentTotal).toFixed(2)),
    [paidAmount, currentTotal]
  );
  const isBalanced = useMemo(
    () => Number(currentTotal.toFixed(2)) === Number(paidAmount.toFixed(2)),
    [currentTotal, paidAmount]
  );

  const handleAmountChange = useCallback((index: number, value: number) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], amount: value };
      return next;
    });
    setShowBalanceError(false);
  }, []);

  const handlePropose = useCallback(async () => {
    if (!isBalanced) {
      setShowBalanceError(true);
      return;
    }
    setSubmitLoading(true);
    try {
      await doAction({
        contractId: milestone.id,
        userId: String(userInfo?.userIdentification || ""),
        actorRole: ESenderRole.CLIENT_ADMIN,
        actorOrgCode: milestone.organizationCode,
        contractAction: EContractActions.CLIENT_RESOLVE_ESCALATION,
        escalationDTO: {
          resolution,
          proposedSettlement: rows
            .filter((r) => r.amount !== undefined)
            .map((r) => ({
              id: r.externalId,
              amountDue: r.amount as number,
              fee: 0,
            })),
        },
      } as any);
      setProposalOpen(false);
      window.location.reload();
    } finally {
      setSubmitLoading(false);
    }
  }, [isBalanced, milestone, userInfo, resolution, rows]);

  const handleAccept = useCallback(async () => {
    setAcceptLoading(true);
    try {
      await doAction({
        contractId: milestone.id,
        userId: String(userInfo?.userIdentification || ""),
        actorRole: ESenderRole.CLIENT_ADMIN,
        actorOrgCode: milestone.organizationCode,
        contractAction: EContractActions.CLIENT_ACCEPT_SETTLEMENT,
      } as any);
      window.location.reload();
    } finally {
      setAcceptLoading(false);
    }
  }, [milestone, userInfo]);

  const handleReject = useCallback(async () => {
    if (!rejectionComment.trim()) return;
    setSubmitLoading(true);
    try {
      await doAction({
        contractId: milestone.id,
        userId: String(userInfo?.userIdentification || ""),
        actorRole: ESenderRole.CLIENT_ADMIN,
        actorOrgCode: milestone.organizationCode,
        contractAction: EContractActions.CLIENT_REJECT_SETTLEMENT,
        escalationDTO: { rejectionComment },
      } as any);
      setRejectOpen(false);
      window.location.reload();
    } finally {
      setSubmitLoading(false);
    }
  }, [milestone, userInfo, rejectionComment]);

  if (!escalation) return null;

  if (
    escalation.status === EEscalationStatus.PENDING_SETTLEMENT ||
    escalation.status === EEscalationStatus.SETTLED
  ) {
    return null;
  }

  if (
    escalation.status === EEscalationStatus.RESOLVED_CUSTOMER ||
    escalation.status === EEscalationStatus.RESOLVED_PROVIDER
  ) {
    const direction =
      escalation.status === EEscalationStatus.RESOLVED_CUSTOMER
        ? t("lbl_favour_customer")
        : t("lbl_favour_provider");

    return (
      <div className={styles.settlementReview}>
        <div className={styles.banner} data-variant="warning">
          <p>{t("lbl_settlement_proposed_review", { direction })}</p>
        </div>

        {(() => {
          const proposed = escalation.proposedSettlement;
          const parties = Array.isArray(proposed)
            ? proposed
            : proposed?.parties ?? [];
          if (parties.length === 0) return null;
          return (
            <div className={styles.settlementBreakdown}>
              {parties.map((entry, i) => (
                <div key={i} className={styles.settlementRow}>
                  <span>{entry.id}</span>
                  <span>
                    {entry.amountDue} {t("lbl_saudi_currency")}
                  </span>
                </div>
              ))}
            </div>
          );
        })()}

        <div className={styles.actions}>
          <OutlinedButton
            onClick={() => setRejectOpen(true)}
            style={{
              width: "180px",
              height: "40px",
              color: "#E32A34",
              background: "#FFF5F5",
              border: "none",
              fontSize: "14px",
              fontWeight: 500,
            }}>
            {t("lbl_reject")}
          </OutlinedButton>
          <PrimaryButton
            disabled={acceptLoading}
            onClick={handleAccept}
            style={{
              width: "180px",
              height: "40px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 500,
              background: acceptLoading ? "#E2E5EE" : "#0D44AD",
            }}>
            {acceptLoading ? <Spinner /> : t("lbl_accept_settlement")}
          </PrimaryButton>
        </div>

        <RejectSettlementModal
          open={rejectOpen}
          onClose={() => {
            setRejectOpen(false);
            setRejectionComment("");
          }}
          onConfirm={handleReject}
          loading={submitLoading}
          rejectionComment={rejectionComment}
          setRejectionComment={setRejectionComment}
        />
      </div>
    );
  }

  // OPEN state
  if (!allowResolveDispute) return null;

  return (
    <div className={styles.settlementReview}>
      {escalation.rejectionComment && (
        <div className={classNames(styles.banner)} data-variant="warning">
          <p>
            {t("lbl_settlement_rejected_reason")}: {escalation.rejectionComment}
          </p>
        </div>
      )}

      <PrimaryButton
        onClick={() => setProposalOpen(true)}
        style={{
          width: "180px",
          height: "40px",
          borderRadius: "8px",
          fontSize: "14px",
          fontWeight: 500,
          background: "#0D44AD",
        }}>
        {t("lbl_propose_settlement")}
      </PrimaryButton>

      {/* Propose settlement modal */}
      <Modal
        open={proposalOpen}
        onClose={() => setProposalOpen(false)}
        className={styles.modalOverlay}>
        <div className={styles.modal}>
          <h2>{t("lbl_propose_settlement")}</h2>

          <div className={styles.balanceRow}>
            <span>{t("lbl_remaining_amount")}:</span>
            <span className={showBalanceError ? styles.errorText : ""}>
              {remaining} {t("lbl_saudi_currency")}
            </span>
          </div>
          {showBalanceError && (
            <p className={styles.errorText}>{t("lbl_settlement_error")}</p>
          )}

          <div className={styles.radioGroup}>
            <label>
              <input
                type="radio"
                checked={resolution === "RESOLVED_CUSTOMER"}
                onChange={() => setResolution("RESOLVED_CUSTOMER")}
              />
              {t("lbl_favour_customer")}
            </label>
            <label>
              <input
                type="radio"
                checked={resolution === "RESOLVED_PROVIDER"}
                onChange={() => setResolution("RESOLVED_PROVIDER")}
              />
              {t("lbl_favour_provider")}
            </label>
          </div>

          {rows.map((row, i) => (
            <div key={i} className={styles.amountRow}>
              <span>
                {row.name || row.externalId} ({row.role})
              </span>
              <input
                type="number"
                min={0}
                max={paidAmount}
                placeholder="0.0"
                value={row.amount ?? ""}
                onChange={(e) => handleAmountChange(i, Number(e.target.value))}
                className={styles.amountInput}
              />
              <span>{t("lbl_saudi_currency")}</span>
            </div>
          ))}

          <div className={styles.modalActions}>
            <OutlinedButton
              onClick={() => setProposalOpen(false)}
              style={{ width: "140px", height: "40px" }}>
              {t("lbl_cancel")}
            </OutlinedButton>
            <PrimaryButton
              disabled={
                rows.every((r) => r.amount === undefined) || submitLoading
              }
              onClick={handlePropose}
              style={{
                width: "180px",
                height: "40px",
                borderRadius: "8px",
                background:
                  rows.every((r) => r.amount === undefined) || submitLoading
                    ? "#9FA6AB"
                    : "#0D44AD",
              }}>
              {submitLoading ? <Spinner /> : t("lbl_propose_settlement")}
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EscalationActions;
